import clsx from 'clsx';
import { Heading } from '~components/Heading';
import { Mark } from '~components/Mark';
import { BaseLink } from '~components/BaseLink';
import { markerRegex, markerSyntax } from '~globals';
import { splitStringAtRegexMatch } from '~utils/string';

import * as styles from './RichTextRenderer.module.css';

import type { ReactNode } from 'react';
import type { NodeRenderer } from '@contentful/rich-text-react-renderer';
import type { HeadingLevel } from '~components/Heading';

export const renderHeading =
  (tagLevel: HeadingLevel, styleLevel: HeadingLevel, props?: React.HTMLAttributes<HTMLHeadingElement>): NodeRenderer =>
  (_, children) => {
    let stringChildren = '';

    if (Array.isArray(children)) {
      const output: string[] = [];
      children.forEach((child) => {
        if (typeof child === 'string') {
          output.push(child);
        } else if (typeof child.props?.children === 'string') {
          output.push(child.props?.children);
        }
      });
      stringChildren = output.join('');
    } else if (typeof children === 'string') {
      stringChildren = children;
    }

    const childArray = splitStringAtRegexMatch(stringChildren, markerRegex);

    return (
      <Heading {...props} className={clsx(styles[`h${styleLevel}`], props?.className)} level={tagLevel}>
        {childArray.map((child) =>
          markerRegex.test(child) ? (
            <Mark key={child} markerColor='var(--green-50)' animated>
              {child.split(markerSyntax)[1]}
            </Mark>
          ) : (
            child
          ),
        )}
      </Heading>
    );
  };

export const renderParagraph =
  (props?: React.HTMLAttributes<HTMLParagraphElement>): NodeRenderer =>
  (_, children) => (
    <p {...props} className={clsx(styles.p, props?.className)}>
      {children}
    </p>
  );

export const renderUlList =
  (props?: React.HTMLAttributes<HTMLUListElement>): NodeRenderer =>
  (_, children) => (
    <ul {...props} className={clsx(styles.ul, props?.className)}>
      {children}
    </ul>
  );

export const renderOlList =
  (props?: React.HTMLAttributes<HTMLOListElement>): NodeRenderer =>
  (_, children) => (
    <ol {...props} className={clsx(styles.ol, props?.className)}>
      {children}
    </ol>
  );

export const renderListItem =
  (props?: React.HTMLAttributes<HTMLLIElement>): NodeRenderer =>
  (_, children) => (
    <li {...props} className={clsx(styles.li, props?.className)}>
      {children}
    </li>
  );

export const renderHr =
  (props?: React.HTMLAttributes<HTMLHRElement>): NodeRenderer =>
  () => <hr {...props} className={clsx(styles.hr, props?.className)} />;

export const renderHyperlink =
  (props?: React.HTMLAttributes<HTMLAnchorElement>): NodeRenderer =>
  (node, children) => (
    <BaseLink {...props} href={node.data.uri}>
      {children}
    </BaseLink>
  );

export const renderBold =
  (props?: React.HTMLAttributes<HTMLElement>): ((text: ReactNode) => ReactNode) =>
  (text) => <strong {...props}>{text}</strong>;

export const renderItalic =
  (props?: React.HTMLAttributes<HTMLElement>): ((text: ReactNode) => ReactNode) =>
  (text) => <em {...props}>{text}</em>;
